import { supabase } from '../lib/supabaseClient';

export interface UKAddress {
  postcode: string;
  formatted_address: string;
  house_number?: string;
  road?: string;
  suburb?: string;
  city: string;
  county?: string;
  streets_from_source?: string;
}

export interface ParsedAddress {
  streetAddress: string;
  town: string;
  postCode: string;
}

/**
 * Search UK addresses by postcode
 */
export async function searchAddressesByPostcode(postcode: string): Promise<UKAddress[]> {
  if (!postcode || postcode.trim().length < 3) {
    console.log('🔍 Postcode too short:', postcode);
    return [];
  }

  try {
    // Clean and format postcode for search
    const cleanPostcode = postcode.trim().toUpperCase();
    console.log('🔍 Searching for postcode:', cleanPostcode);
    
    const { data, error } = await supabase
      .from('uk_addresses')
      .select(`
        postcode,
        formatted_address,
        house_number,
        road,
        suburb,
        city,
        county,
        streets_from_source
      `)
      .ilike('postcode', `${cleanPostcode}%`)
      .order('postcode')
      .limit(20); // Limit results to prevent overwhelming UI

    if (error) {
      console.error('❌ Error searching UK addresses:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log(`✅ Found ${data?.length || 0} addresses for "${cleanPostcode}"`); 
    if (data && data.length > 0) {
      console.log('Sample result:', data[0]);
    }

    return data || [];
  } catch (error) {
    console.error('❌ Exception in searchAddressesByPostcode:', error);
    throw error;
  }
}

/**
 * Get unique addresses for a specific postcode (removes duplicates)
 */
export async function getUniqueAddressesForPostcode(postcode: string): Promise<UKAddress[]> {
  if (!postcode || postcode.trim().length < 5) {
    return [];
  }

  try {
    const cleanPostcode = postcode.trim().toUpperCase();
    
    const { data, error } = await supabase
      .from('uk_addresses')
      .select(`
        postcode,
        formatted_address,
        house_number,
        road,
        suburb,
        city,
        county,
        streets_from_source
      `)
      .eq('postcode', cleanPostcode)
      .order('road, house_number');

    if (error) {
      console.error('Error fetching addresses for postcode:', error);
      throw error;
    }

    // Remove duplicates based on street address
    const uniqueAddresses = (data || []).reduce((acc: UKAddress[], current: UKAddress) => {
      const streetAddress = parseStreetAddress(current);
      const isDuplicate = acc.some(addr => 
        parseStreetAddress(addr) === streetAddress
      );
      
      if (!isDuplicate) {
        acc.push(current);
      }
      
      return acc;
    }, []);

    return uniqueAddresses;
  } catch (error) {
    console.error('Error in getUniqueAddressesForPostcode:', error);
    throw error;
  }
}

/**
 * Parse formatted address to extract location details
 * Example format: "London Borough of Croydon, Greater London, England, CR2 6DT, United Kingdom"
 */
function parseFormattedAddress(formatted: string, postcode: string): { city: string; county: string; country: string } {
  try {
    // Remove postcode and country suffix from formatted address
    let cleaned = formatted
      .replace(postcode, '')
      .replace(/,\s*United Kingdom\s*$/, '')
      .replace(/,\s*England\s*,?/, ',') // Remove country but keep as marker
      .replace(/,\s*Scotland\s*,?/, ',')
      .replace(/,\s*Wales\s*,?/, ',')
      .replace(/,\s*Northern Ireland\s*,?/, '')
      .trim();

    // Split by comma and clean up parts
    const parts = cleaned
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0);

    let city = '';
    let county = '';
    let country = '';

    // Extract country from original formatted address
    if (formatted.includes('England')) country = 'England';
    else if (formatted.includes('Scotland')) country = 'Scotland';
    else if (formatted.includes('Wales')) country = 'Wales';
    else if (formatted.includes('Northern Ireland')) country = 'Northern Ireland';

    // Parse location parts
    if (parts.length >= 2) {
      // Format: [Borough/District], [County]
      city = parts[0];
      county = parts[1];
    } else if (parts.length === 1) {
      // Only one location available
      city = parts[0];
    }

    // Clean up common UK borough/district prefixes
    city = city
      .replace(/^London Borough of\s+/i, '')
      .replace(/^Royal Borough of\s+/i, '')
      .replace(/^City of\s+/i, '')
      .replace(/^Borough of\s+/i, '')
      .replace(/^Metropolitan Borough of\s+/i, '')
      .trim();

    return { city, county, country };
  } catch (error) {
    console.error('Error parsing formatted address:', error);
    return { city: '', county: '', country: '' };
  }
}

/**
 * Parse UK address data into form fields
 */
export function parseAddressForForm(address: UKAddress): ParsedAddress {
  const streetAddress = parseStreetAddress(address);
  
  // Get city/town - prefer structured data, fallback to formatted address
  let town = address.city || address.suburb || '';
  
  // If no structured city data, try to parse from formatted_address
  if (!town && address.formatted_address) {
    const parsed = parseFormattedAddress(address.formatted_address, address.postcode);
    town = parsed.city || parsed.county || '';
  }
  
  return {
    streetAddress,
    town,
    postCode: address.postcode
  };
}

/**
 * Parse street address from UK address data
 */
function parseStreetAddress(address: UKAddress): string {
  const parts: string[] = [];
  
  // Add house number if available
  if (address.house_number) {
    parts.push(address.house_number);
  }
  
  // Add road name
  if (address.road) {
    parts.push(address.road);
  } else if (address.streets_from_source) {
    // Use first street from source if no main road
    const firstStreet = address.streets_from_source.split(',')[0];
    if (firstStreet) {
      parts.push(firstStreet.trim());
    }
  }
  
  return parts.join(' ').trim();
}

/**
 * Format address for display in dropdown
 */
export function formatAddressForDisplay(address: UKAddress): string {
  const streetAddress = parseStreetAddress(address);
  
  // Get location - prefer structured data, fallback to formatted address
  let location = [address.city, address.county].filter(Boolean).join(', ');
  
  // If no structured location data, parse from formatted_address
  if (!location && address.formatted_address) {
    const parsed = parseFormattedAddress(address.formatted_address, address.postcode);
    const parts = [parsed.city, parsed.county].filter(Boolean);
    location = parts.join(', ');
  }
  
  // Build address with postcode
  const parts = [];
  if (streetAddress) parts.push(streetAddress);
  if (location) parts.push(location);
  if (address.postcode) parts.push(address.postcode);
  
  if (parts.length > 0) {
    return parts.join(', ');
  } else {
    return address.formatted_address || address.postcode;
  }
}

/**
 * Validate UK postcode format
 */
export function isValidUKPostcode(postcode: string): boolean {
  const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
  return ukPostcodeRegex.test(postcode.trim());
}
